import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  CheckCircle,
  Users,
  Gauge,
  Timer,
  Maximize,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";
import { useApi } from "../lib/api";
import {
  KONE_LIFT_DIMENSIONS,
  findKoneLiftOption,
  toSquareMetres,
} from "../data/koneLiftDimensions";
import DownloadLiftReportBtn from "./pdf/DownloadLiftReportBtn";

import "../styles/lift-calculator.css";

const DEFAULT_OCCUPANCY_RATIO = 10;
const DEFAULT_FLOOR_HEIGHT_M = 3.9;
const DEFAULT_LIFT_SPEEDS = [1.0, 1.6, 2.0, 2.5, 3.0, 3.5, 4.0];
const PASSENGER_PROFILE = { profile: "office" };
const BCO_WAIT_TARGET_SECONDS = 25;
const CIBSE_WAIT_TARGET_SECONDS = 30;
const WAIT_CHART_TARGET_SECONDS = CIBSE_WAIT_TARGET_SECONDS;
const WAIT_CHART_MAX_SECONDS = 50;

const formatNumber = (value, digits = 1) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  const fixed = num.toFixed(digits);
  return digits > 0 ? fixed.replace(/\.0+$/, "") : fixed;
};

const toErrorMessage = (err) => {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  if (err.detail) {
    if (typeof err.detail === "string") return err.detail;
    if (Array.isArray(err.detail) && err.detail[0]?.msg) return err.detail[0].msg;
  }
  if (typeof err.message === "string" && err.message !== "[object Object]") {
    return err.message;
  }
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
};

function buildLiftSizesLibrary() {
  const entries = {};
  Object.entries(KONE_LIFT_DIMENSIONS || {}).forEach(([label, options]) => {
    if (!label) return;
    const parsed = parseLiftLabel(label);
    const configs = Array.isArray(options) && options.length ? options : [];
    const candidate = configs.reduce((best, option) => {
      if (!option?.car) return best;
      const area = toSquareMetres(option.car.width_mm, option.car.depth_mm);
      if (!Number.isFinite(area)) return best;
      if (!best || area < best.area) {
        return {
          area,
          width_mm: option.car.width_mm,
          depth_mm: option.car.depth_mm,
        };
      }
      return best;
    }, null);

    entries[label] = {
      persons: parsed?.persons ?? 8,
      rated_kg: parsed?.kg ?? (parsed?.persons ?? 8) * 80,
      area: Number((candidate?.area ?? 1.5).toFixed(3)),
    };
  });
  return entries;
}

function parseLiftLabel(label) {
  if (typeof label !== "string") return null;
  const match = label.match(/(\d+)\s*Persons?\s*\/\s*(\d+)/i);
  if (!match) return null;
  return {
    persons: Number(match[1]),
    kg: Number(match[2]),
  };
}

function buildFloorSeries(rows) {
  if (!Array.isArray(rows) || rows.length < 2) return null;
  const sorted = rows.slice().sort((a, b) => a.floor_index - b.floor_index);
  const areas = [];
  const heights = [];
  let hasPopulation = false;

  sorted.forEach((row) => {
    const nia = Number(row?.nia_m2);
    const area = Number.isFinite(nia) && nia > 0 ? nia : 0;
    if (area > 0) hasPopulation = true;
    areas.push(area);

    const height = Number(row?.floor_to_floor_height_m);
    heights.push(Number.isFinite(height) && height > 0 ? height : DEFAULT_FLOOR_HEIGHT_M);
  });

  let entranceFloor = sorted.findIndex((row) => row?.is_entrance_floor);
  if (entranceFloor < 0) entranceFloor = 0;

  return {
    numFloors: sorted.length,
    areas,
    heights,
    entranceFloor,
    hasPopulation,
  };
}

function floorSeriesFromHistory(details) {
  if (!details) return null;
  const areas = Array.isArray(details.floor_areas) ? details.floor_areas : null;
  if (!areas || areas.length < 2) return null;
  const heights = Array.isArray(details.floor_heights) ? details.floor_heights : [];
  const rawEntrance = Number(details.entrance_floor);
  const entrance = Number.isFinite(rawEntrance) ? rawEntrance : 0;
  const cleanedAreas = areas.map((area) => {
    const val = Number(area);
    return Number.isFinite(val) && val >= 0 ? val : 0;
  });
  const cleanedHeights = cleanedAreas.map((_, idx) => {
    const val = Number(heights[idx]);
    return Number.isFinite(val) && val > 0 ? val : DEFAULT_FLOOR_HEIGHT_M;
  });
  const hasPopulation = cleanedAreas.some((area) => area > 0);
  const entranceFloor = entrance >= 0 && entrance < cleanedAreas.length ? entrance : 0;
  return {
    numFloors: cleanedAreas.length,
    areas: cleanedAreas,
    heights: cleanedHeights,
    entranceFloor,
    hasPopulation,
  };
}

const LIFT_SIZE_LIBRARY = buildLiftSizesLibrary();

const formatSeconds = (value) => `${value.toFixed(1)}s`;

const useChartDimensions = (data) => {
  return useMemo(() => {
    if (!data?.length) {
      return {
        lifts: [0, 1],
        waitRange: [0, WAIT_CHART_MAX_SECONDS],
      };
    }

    const lifts = data.map((d) => d.lifts);
    const waits = data.map((d) => d.wait);
    const minLift = Math.min(...lifts);
    const maxLift = Math.max(...lifts);
    const maxWait = Math.max(WAIT_CHART_TARGET_SECONDS, ...waits);
    const paddedMax = Math.max(WAIT_CHART_MAX_SECONDS, Math.ceil(maxWait / 10) * 10);

    return {
      lifts: [minLift, maxLift === minLift ? minLift + 1 : maxLift],
      waitRange: [0, paddedMax],
    };
  }, [data]);
};

const WaitCurveChart = ({ data }) => {
  if (!data?.length) {
    return (
      <div className="pd-chart-card">
        <p className="pd-muted">Run the simulation to view the curve.</p>
      </div>
    );
  }

  const bounds = useChartDimensions(data);
  const width = 720;
  const height = 280;
  const padding = { top: 20, right: 24, bottom: 48, left: 60 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  const toX = (value) => {
    const [minLift, maxLift] = bounds.lifts;
    const span = maxLift - minLift || 1;
    const ratio = (value - minLift) / span;
    return padding.left + ratio * plotWidth;
  };

  const toY = (value) => {
    const [minWait, maxWait] = bounds.waitRange;
    const ratio = (value - minWait) / (maxWait - minWait || 1);
    return height - padding.bottom - ratio * plotHeight;
  };

  const tickValues = Array.from(
    { length: Math.floor(bounds.waitRange[1] / 10) },
    (_, idx) => (idx + 1) * 10,
  );

  const xTicks = data.map((point) => point.lifts);

  const linePath = data
    .map((point, index) => {
      const command = index === 0 ? "M" : "L";
      return `${command}${toX(point.lifts)},${toY(point.wait)}`;
    })
    .join(" ");

  return (
    <div className="pd-chart-card">
      <div className="pd-chart-card__header">
        <div>
          <h4 className="pd-h4">Average waiting time</h4>
          <p className="pd-muted">
            Visual summary of lift count versus passenger waiting time.
          </p>
        </div>
        <div className="pd-chart-meta">CIBSE target ≤ {WAIT_CHART_TARGET_SECONDS}s</div>
      </div>

      <svg
        className="pd-wait-chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Graph showing the relationship between lift count and average waiting time"
      >
        <defs>
          <linearGradient id="pdChartLine" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#1f6feb" />
            <stop offset="100%" stopColor="#00a896" />
          </linearGradient>
        </defs>

        {/* compliant zone shading */}
        <rect
          className="pd-chart-zone"
          x={padding.left}
          width={plotWidth}
          y={toY(WAIT_CHART_TARGET_SECONDS)}
          height={Math.max(0, height - padding.bottom - toY(WAIT_CHART_TARGET_SECONDS))}
        />

        {/* grid lines */}
        {tickValues.map((tick) => (
          <g key={`grid-${tick}`} className="pd-chart-grid-row">
            <line x1={padding.left} x2={width - padding.right} y1={toY(tick)} y2={toY(tick)} />
            <text x={padding.left - 12} y={toY(tick) + 4}>
              {tick}
            </text>
          </g>
        ))}

        {/* reference line */}
        <g aria-label={`Target wait line at ${WAIT_CHART_TARGET_SECONDS} seconds`}>
          <line
            className="pd-chart-ref"
            x1={padding.left}
            x2={width - padding.right}
            y1={toY(WAIT_CHART_TARGET_SECONDS)}
            y2={toY(WAIT_CHART_TARGET_SECONDS)}
          />
        </g>

        {/* path */}
        <path className="pd-chart-line" d={linePath} />

        {/* points */}
        {data.map((point) => (
          <g key={`point-${point.lifts}`} className="pd-chart-point">
            <circle
              cx={toX(point.lifts)}
              cy={toY(point.wait)}
              r={7}
              className={point.compliant ? "is-compliant" : ""}
            />
            <text x={toX(point.lifts)} y={toY(point.wait) - 14}>
              {formatSeconds(point.wait)}
            </text>
          </g>
        ))}

        {/* X axis */}
        <line
          x1={padding.left}
          x2={width - padding.right}
          y1={height - padding.bottom}
          y2={height - padding.bottom}
          className="pd-chart-axis"
        />
        {xTicks.map((tick) => (
          <g key={`xtick-${tick}`}>
            <line
              x1={toX(tick)}
              x2={toX(tick)}
              y1={height - padding.bottom}
              y2={height - padding.bottom + 8}
              className="pd-chart-axis"
            />
            <text x={toX(tick)} y={height - padding.bottom + 24}>
              {tick}
            </text>
          </g>
        ))}
        <text
          className="pd-chart-axis-label"
          x={(width + padding.left) / 2}
          y={height - 8}
        >
          Number of lifts
        </text>
        <text
          className="pd-chart-axis-label"
          x={16}
          y={padding.top}
          transform={`rotate(-90 16 ${padding.top})`}
        >
          Avg wait (seconds)
        </text>
      </svg>

      <div className="pd-chart-summary" aria-live="polite">
        {data.map((point) => (
          <div key={`summary-${point.lifts}`}> 
            <span className="pd-chart-summary__label">
              {point.lifts} lifts
            </span>
            <strong>{formatSeconds(point.wait)}</strong>
            <span
              className={`pd-chart-summary__status ${
                point.compliant ? "is-good" : "is-bad"
              }`}
            >
              {point.compliant ? "Compliant" : "Review"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------------- Main Calculator Page ---------------- */

export default function LiftTrafficCalculator() {
  const api = useApi();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const rawUsageEventId = searchParams.get("usage_event_id");
  const usageEventId = rawUsageEventId ? rawUsageEventId.trim() : "";
  const usageEventPath = usageEventId
    ? `/usage-events/${encodeURIComponent(usageEventId)}`
    : "";

  const projectId =
    params.id ||
    searchParams.get("project_id") ||
    searchParams.get("projectId") ||
    searchParams.get("project") ||
    null;

  // inputs
  const [destinationControl, setDestinationControl] = useState(true);
  const [autoLiftRange, setAutoLiftRange] = useState(true);
  const [minLifts, setMinLifts] = useState(3);
  const [maxLifts, setMaxLifts] = useState(8);
  const [trafficScenario, setTrafficScenario] = useState("morning");

  // app state
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [runError, setRunError] = useState("");

  const [project, setProject] = useState(null);
  const [projectInputs, setProjectInputs] = useState([]);
  const [projectLoading, setProjectLoading] = useState(!!projectId);
  const [projectError, setProjectError] = useState("");
  const [historyEvent, setHistoryEvent] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadProject(pid) {
      try {
        setProjectLoading(true);
        setProjectError("");
        setRunError("");
        setResult(null);
        const [proj, inputs] = await Promise.all([
          api(`/projects/${pid}`),
          api(`/projects/${pid}/inputs`),
        ]);
        if (!active) return;
        setProject(proj || null);
        const sorted = Array.isArray(inputs)
          ? inputs.slice().sort((a, b) => a.floor_index - b.floor_index)
          : [];
        setProjectInputs(sorted);
      } catch (err) {
        if (!active) return;
        setProject(null);
        setProjectInputs([]);
        setProjectError(toErrorMessage(err) || "Failed to load project");
      } finally {
        if (active) setProjectLoading(false);
      }
    }

    if (projectId) {
      loadProject(projectId);
    } else {
      setProject(null);
      setProjectInputs([]);
      setProjectError("");
      setProjectLoading(false);
      if (!usageEventId) {
        setResult(null);
        setRunError("");
      }
    }

    return () => {
      active = false;
    };
  }, [api, projectId, usageEventId]);

  useEffect(() => {
    let active = true;
    if (!usageEventPath) {
      setHistoryEvent(null);
      setHistoryLoading(false);
      setHistoryError("");
      return () => {
        active = false;
      };
    }

    async function loadHistory() {
      try {
        setHistoryLoading(true);
        setHistoryError("");
        const data = await api(usageEventPath);
        if (!active) return;
        setHistoryEvent(data || null);
      } catch (err) {
        if (!active) return;
        setHistoryEvent(null);
        setHistoryError(toErrorMessage(err));
      } finally {
        if (active) setHistoryLoading(false);
      }
    }

    loadHistory();
    return () => {
      active = false;
    };
  }, [api, usageEventId, usageEventPath]);

  useEffect(() => {
    if (!historyEvent?.details) return;
    const details = historyEvent.details;
    if (typeof details.destination_control === "boolean") {
      setDestinationControl(details.destination_control);
    }
    const hasManualRange =
      details.min_elevators != null || details.max_elevators != null;
    setAutoLiftRange(!hasManualRange);
    if (typeof details.min_elevators === "number") {
      setMinLifts(details.min_elevators);
    }
    if (typeof details.max_elevators === "number") {
      setMaxLifts(details.max_elevators);
    }
    if (typeof details.traffic_profile === "string") {
      setTrafficScenario(details.traffic_profile);
    }
  }, [historyEvent]);

  useEffect(() => {
    if (!historyEvent?.result_json) return;
    const floors = floorSeriesFromHistory(historyEvent.details);
    setResult({
      response: historyEvent.result_json,
      floorMeta: floors
        ? { numFloors: floors.numFloors, entranceFloor: floors.entranceFloor }
        : null,
    });
  }, [historyEvent]);

  const historyFloorSeries = useMemo(
    () => floorSeriesFromHistory(historyEvent?.details),
    [historyEvent],
  );
  const floorSeries = useMemo(() => {
    if (historyFloorSeries) return historyFloorSeries;
    return buildFloorSeries(projectInputs);
  }, [historyFloorSeries, projectInputs]);
  const occupancyRatio = useMemo(() => {
    const fromHistory = Number(historyEvent?.details?.occupancy_ratio);
    if (Number.isFinite(fromHistory) && fromHistory > 0) return fromHistory;
    const val = Number(project?.occupancy_ratio);
    return Number.isFinite(val) && val > 0 ? val : DEFAULT_OCCUPANCY_RATIO;
  }, [historyEvent, project?.occupancy_ratio]);

  const canRunSimulation = Boolean(
    !historyLoading &&
    (!projectId || !projectLoading || historyFloorSeries) &&
    floorSeries &&
    floorSeries.numFloors >= 2 &&
    floorSeries.hasPopulation,
  );

  const runDisabledReason = (() => {
    if (historyLoading) return "Loading saved inputs…";
    if (projectId && projectLoading && !historyFloorSeries)
      return "Loading project data…";
    if (!floorSeries)
      return usageEventId
        ? "Saved run is missing floor data. Re-run to regenerate inputs."
        : "Add floor areas and heights to this project.";
    if (!floorSeries.hasPopulation)
      return "Enter NIA for at least one occupied floor.";
    return "";
  })();

  const handleRunSimulation = useCallback(async () => {
    if (!floorSeries || !canRunSimulation) {
      setRunError(runDisabledReason || "Project data incomplete");
      return;
    }

    setIsRunning(true);
    setRunError("");
    setResult(null);

    const minElevators = autoLiftRange ? null : Math.max(1, Math.round(minLifts));
    const maxElevators = autoLiftRange
      ? null
      : Math.max(minElevators ?? 1, Math.round(maxLifts));

    const payload = {
      num_floors: floorSeries.numFloors,
      floor_heights: floorSeries.heights,
      entrance_floor: floorSeries.entranceFloor,
      floor_areas: floorSeries.areas,
      occupancy_ratio: occupancyRatio,
      min_elevators: minElevators,
      max_elevators: maxElevators,
      lift_sizes: LIFT_SIZE_LIBRARY,
      lift_speeds: DEFAULT_LIFT_SPEEDS,
      passenger_data: PASSENGER_PROFILE,
      destination_control: destinationControl,
      traffic_profile: trafficScenario,
    };

    try {
      const query = projectId ? `?project_id=${projectId}` : "";
      const response = await api(`/calc/lifts/run${query}`, {
        method: "POST",
        body: payload,
        project_id: projectId,
      });
      setResult({
        response,
        floorMeta: {
          numFloors: floorSeries.numFloors,
          entranceFloor: floorSeries.entranceFloor,
        },
      });
      toast.success("Simulation complete");
    } catch (err) {
      const message = err?.message || "Failed to run simulation";
      setRunError(message);
      toast.error(message);
    } finally {
      setIsRunning(false);
    }
  }, [
    api,
    autoLiftRange,
    canRunSimulation,
    destinationControl,
    floorSeries,
    maxLifts,
    minLifts,
    occupancyRatio,
    projectId,
    runDisabledReason,
    trafficScenario,
  ]);

  const handleBack = useCallback(() => {
    if (projectId) {
      navigate(`/projects/${projectId}`);
    } else {
      navigate(-1);
    }
  }, [navigate, projectId]);

  const calcMeta = {
    title: "Lift Traffic Analysis & Simulation",
  };

  const projectName = historyEvent?.project_name
    ? historyEvent.project_name
    : project?.name
    ? project.name
    : projectId
    ? projectLoading
      ? "Loading…"
      : "—"
    : usageEventId
    ? "History run"
    : "Select a project";
  const buildingUse = project?.building_use || "—";
  const floorsDisplay = floorSeries?.numFloors ?? project?.floors_count ?? "—";
  const occupancyLabel = `${formatNumber(occupancyRatio, 1)} m² / person`;

  return (
    <div className="pd-calculator-page">
      <section className="pd-section pd-calculator">
        <div className="pd-container narrow">
          {/* ------------ Header row ------------ */}
          <div className="pd-calculator__header">
            <div>
              <h1 className="pd-h2">{calcMeta.title}</h1>
            </div>

            <div className="pd-actions">
              <DownloadLiftReportBtn
                project={project}
                inputs={floorSeries}
                result={result}
                disabled={isRunning || !result}
              />
              <button
                type="button"
                className="pd-btn pd-btn--primary"
                onClick={handleRunSimulation}
                disabled={isRunning || !canRunSimulation}
              >
                {isRunning && (
                  <span className="pd-spin-icon">
                    <RefreshCw size={16} />
                  </span>
                )}
                {!isRunning && (
                  <span className="pd-run-icon" aria-hidden="true">
                    ▶
                  </span>
                )}
                {isRunning ? "Running…" : "Run simulation"}
              </button>
              <button
                type="button"
                className="pd-btn pd-btn--ghost"
                onClick={handleBack}
              >
                <ArrowLeft size={14} />
                Back
              </button>
            </div>
          </div>

          {/* ------------ Project details ------------ */}
          <div className="pd-block">
            <h3 className="pd-h3">Project details</h3>
            {usageEventId && (
              <div className="pd-alert pd-alert--info">
                {historyLoading && "Loading saved run…"}
                {!historyLoading && historyEvent && (
                  <span>
                    Reopened run from {(() => {
                      const parsed = new Date(historyEvent.created_at);
                      return Number.isNaN(parsed.valueOf())
                        ? historyEvent.created_at
                        : parsed.toLocaleString();
                    })()}
                  </span>
                )}
                {!historyLoading && historyError && (
                  <span>Failed to load saved run: {historyError}</span>
                )}
              </div>
            )}
            {projectLoading && (
              <p className="pd-muted">Loading project data…</p>
            )}
            {projectError && !projectLoading && (
              <div className="pd-alert">
                <strong>Error:</strong> {projectError}
              </div>
            )}
            {!projectId && !historyFloorSeries && !projectLoading && (
              <p className="pd-muted">
                Open the calculator from a project to prefill building data.
              </p>
            )}
            <div className="pd-project-detail-grid">
              <ProjectDetailItem
                label="Project name"
                value={projectName}
              />
              <ProjectDetailItem
                label="Number of floors"
                value={floorsDisplay}
              />
              <ProjectDetailItem
                label="Building use"
                value={buildingUse}
              />
              <ProjectDetailItem
                label="Occupancy ratio"
                value={occupancyLabel}
              />
            </div>
            {!canRunSimulation && runDisabledReason && (
              <p className="pd-muted pd-help-text">{runDisabledReason}</p>
            )}
          </div>

          {/* ------------ Input parameters ------------ */}
          <div className="pd-block">
            <h3 className="pd-h3">Input parameters</h3>

            <div className="pd-input-grid">
              {/* Destination control */}
              <div className="pd-form-group">
                <span className="pd-label">Destination control</span>
                <label
                  className={`pd-toggle-button ${
                    destinationControl ? "active" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={destinationControl}
                    onChange={(e) => setDestinationControl(e.target.checked)}
                  />
                  <span>Destination control</span>
                </label>
                <p className="pd-muted pd-help-text">
                  Applies a performance uplift for destination-control
                  configurations.
                </p>
              </div>

              {/* Lift range */}
              <div className="pd-form-group">
                <span className="pd-label">Number of lifts</span>

                <div className="pd-toggle-group">
                  <label
                    className={`pd-toggle-button ${
                      autoLiftRange ? "active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="lift_range"
                      checked={autoLiftRange}
                      onChange={() => setAutoLiftRange(true)}
                    />
                    <span>Let Pebbledash pick a sensible range</span>
                  </label>

                  <label
                    className={`pd-toggle-button ${
                      !autoLiftRange ? "active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="lift_range"
                      checked={!autoLiftRange}
                      onChange={() => setAutoLiftRange(false)}
                    />
                    <span>Advanced</span>
                  </label>
                </div>

                {!autoLiftRange && (
                  <div className="pd-lift-range">
                    <div className="pd-lift-range__field">
                      <label htmlFor="min_lifts" className="pd-label-small">
                        Min
                      </label>
                      <input
                        id="min_lifts"
                        type="number"
                        value={minLifts}
                        onChange={(e) => setMinLifts(Number(e.target.value))}
                      />
                    </div>
                    <div className="pd-lift-range__field">
                      <label htmlFor="max_lifts" className="pd-label-small">
                        Max
                      </label>
                      <input
                        id="max_lifts"
                        type="number"
                        value={maxLifts}
                        onChange={(e) => setMaxLifts(Number(e.target.value))}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Traffic scenario */}
              <div className="pd-form-group">
                <span className="pd-label">Traffic scenario</span>
                <div className="pd-toggle-group">
                  <label
                    className={`pd-toggle-button ${
                      trafficScenario === "morning" ? "active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="traffic_scenario"
                      checked={trafficScenario === "morning"}
                      onChange={() => setTrafficScenario("morning")}
                    />
                    <span>Morning peak</span>
                  </label>

                  <label
                    className={`pd-toggle-button ${
                      trafficScenario === "lunch" ? "active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="traffic_scenario"
                      checked={trafficScenario === "lunch"}
                      onChange={() => setTrafficScenario("lunch")}
                    />
                    <span>Lunch peak</span>
                  </label>
                </div>
                <p className="pd-muted pd-help-text">
                  Morning peak is standard for lift sizing. Lunch peak is a
                  more complex 2-way stress test.
                </p>
              </div>
            </div>
          </div>

          {/* ------------ Notes ------------ */}
          <div className="pd-block">
            <h3 className="pd-h3">Notes</h3>
            <p className="pd-muted pd-notes-row">
              Simulation is based on CIBSE Guide D (2020) for modern office
              buildings. Morning peak sizing aligns with BCO guidance, while
              lunch peak provides a two-way stress test.
            </p>
            {runError && (
              <div className="pd-alert">
                <strong>Error:</strong> {runError}
              </div>
            )}
          </div>

          {/* ------------ Results ------------ */}
          <div className="pd-block">
            <h3 className="pd-h3">Results centre</h3>

            <div className="pd-results-shell">
              {isRunning && (
                <div className="pd-results-state">
                  <RefreshCw size={24} className="pd-spin" />
                  <p>Running simulation…</p>
                </div>
              )}

              {!isRunning && !result && (
                <div className="pd-results-state">
                  <p>Run the simulation to see results.</p>
                </div>
              )}

              {!isRunning && result && (
                <LiftResultsDashboard result={result} />
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------------- Small helper ---------------- */

function ProjectDetailItem({ label, value }) {
  return (
    <div className="pd-project-detail-item">
      <span className="pd-label-small">{label}</span>
      <span className="pd-project-detail-value">{value}</span>
    </div>
  );
}

function findCibseRecommendation(allResults) {
  if (!Array.isArray(allResults)) return null;
  const candidates = allResults.filter((item) => item?.cibse_compliant);
  if (!candidates.length) return null;

  const toNumber = (value) => (Number.isFinite(value) ? value : Number.POSITIVE_INFINITY);

  return candidates
    .slice()
    .sort((a, b) => {
      if (Boolean(a?.bco_compliant) !== Boolean(b?.bco_compliant)) {
        return a?.bco_compliant ? -1 : 1;
      }

      const liftsDiff = toNumber(a?.num_lifts) - toNumber(b?.num_lifts);
      if (liftsDiff !== 0) return liftsDiff;

      const areaDiff = toNumber(a?.total_shaft_area_sqm) - toNumber(b?.total_shaft_area_sqm);
      if (areaDiff !== 0) return areaDiff;

      const waitA = toNumber(a?.performance?.avg_wait_time_s);
      const waitB = toNumber(b?.performance?.avg_wait_time_s);
      return waitA - waitB;
    })[0];
}

function selectRecommendation(simulation) {
  if (!simulation) return null;
  const serverRecommendation = simulation.recommendation;
  if (serverRecommendation?.cibse_compliant) {
    return serverRecommendation;
  }

  const cibseFallback = findCibseRecommendation(simulation.all_results);
  if (cibseFallback) {
    return cibseFallback;
  }

  return serverRecommendation || null;
}

/* =================================================================== */
/*                        LIFT RESULTS DASHBOARD                       */
/* =================================================================== */

function LiftResultsDashboard({ result }) {
  const [activeTab, setActiveTab] = useState("heatmap");
  const simulation = result?.response || {};
  const floorMeta = result?.floorMeta || {};
  const recommendation = useMemo(() => selectRecommendation(simulation), [simulation]);

  const curveData = useMemo(() => buildCurveData(simulation?.all_results), [simulation]);
  const heatmapData = useMemo(() => buildHeatmapData(simulation?.all_results), [simulation]);

  const avgWait = recommendation?.performance?.avg_wait_time_s;
  const avgWaitTone = Number.isFinite(avgWait)
    ? avgWait < BCO_WAIT_TARGET_SECONDS
      ? "good"
      : avgWait < CIBSE_WAIT_TARGET_SECONDS
      ? "warn"
      : "bad"
    : undefined;
  const carLoadFraction = recommendation?.performance?.avg_car_load_fraction;
  const carLoadPercent = Number.isFinite(carLoadFraction)
    ? Math.round(carLoadFraction * 100)
    : null;
  const carLoadClass = carLoadPercent != null && carLoadPercent > 80 ? "is-high" : "is-healthy";
  const carLoadNote =
    carLoadPercent == null
      ? "Run the simulation to view loading."
      : carLoadPercent > 80
      ? "Risk of saturation."
      : "Within healthy loading range.";

  const complianceBadge = (() => {
    if (!recommendation) return "Awaiting recommendation";
    if (recommendation.bco_compliant && recommendation.cibse_compliant)
      return "BCO & CIBSE compliant";
    if (recommendation.cibse_compliant) return "CIBSE compliant";
    return "Review performance";
  })();

  const recommendedDimensions = useMemo(
    () => deriveLiftDimensions(recommendation?.lift_size_key, recommendation?.speed_mps),
    [recommendation?.lift_size_key, recommendation?.speed_mps],
  );

  const liftMeta = recommendation?.lift_size_key
    ? LIFT_SIZE_LIBRARY?.[recommendation.lift_size_key]
    : null;

  const liftCountLabel = Number.isFinite(Number(recommendation?.num_lifts))
    ? `${recommendation.num_lifts} lifts`
    : "—";

  const liftCapacityLabel = liftMeta
    ? `${liftMeta.persons} person / ${liftMeta.rated_kg} kg`
    : recommendation?.lift_size_key || "Select a lift size";

  return (
    <div className="pd-lift-results">
      <div className="pd-lift-hero">
        <div className="pd-lift-hero__header">
          <div className="pd-lift-hero__title">
            <CheckCircle size={18} />
            <span>Recommended configuration</span>
          </div>
          <div className="pd-lift-hero__badge">{complianceBadge}</div>
        </div>
        {simulation?.goods_lift_required && (
          <div className="pd-lift-hero__badge pd-lift-hero__badge--warn">
            Goods/service lift recommended
          </div>
        )}

        {recommendation ? (
          <div className="pd-lift-hero__grid">
            <StatBox
              icon={<Users size={16} />}
              label="Configuration"
              value={liftCountLabel}
              sub={liftCapacityLabel}
            />
            <StatBox
              icon={<Gauge size={16} />}
              label="Speed"
              value={
                Number.isFinite(recommendation.speed_mps)
                  ? `${recommendation.speed_mps.toFixed(1)} m/s`
                  : "—"
              }
              sub="High-performance office"
            />
            <StatBox
              icon={<Timer size={16} />}
              label="Average wait"
              value={
                Number.isFinite(avgWait) ? `${avgWait.toFixed(1)}s` : "Run the simulation"
              }
              sub="BCO: < 25s · CIBSE: < 30s"
              tone={avgWaitTone}
            />

            <div className="pd-carload">
              <div className="pd-carload__label-row">
                <span>Average car load</span>
                <span>{carLoadPercent != null ? `${carLoadPercent}%` : "—"}</span>
              </div>
              <div className="pd-carload-bar">
                <div
                  className={`pd-carload-bar__fill ${carLoadClass}`}
                  style={{ width: `${carLoadPercent ?? 0}%` }}
                />
              </div>
              <p className="pd-carload__note">{carLoadNote}</p>
            </div>
          </div>
        ) : (
          <p className="pd-muted" style={{ marginTop: 12 }}>
            No compliant configuration was found. Review the heatmap for alternative options.
          </p>
        )}
      </div>

      <div className="pd-tabs">
        <button
          type="button"
          className={`pd-tab ${activeTab === "heatmap" ? "is-active" : ""}`}
          onClick={() => setActiveTab("heatmap")}
        >
          Solution heatmap
        </button>
        <button
          type="button"
          className={`pd-tab ${activeTab === "overview" ? "is-active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          Performance curve
        </button>
        <button
          type="button"
          className={`pd-tab ${activeTab === "zoning" ? "is-active" : ""}`}
          onClick={() => setActiveTab("zoning")}
        >
          Zoning strategy
        </button>
      </div>

      <div className="pd-panel pd-panel--chart">
        {activeTab === "heatmap" && <SolutionHeatmap data={heatmapData} />}
        {activeTab === "overview" && <WaitCurveChart data={curveData} />}
        {activeTab === "zoning" && (
          <ZoningStrategy
            numFloors={floorMeta.numFloors}
            entranceFloor={floorMeta.entranceFloor}
            numLifts={recommendation?.num_lifts || 0}
            highRiseStart={simulation?.high_rise_start_floor}
            dropOff={simulation?.dropoff_floor}
          />
        )}
      </div>

      {recommendedDimensions && (
        <div className="pd-panel">
          <h3 className="pd-panel-title">
            <Maximize size={14} />
            Recommended lift dimensions (per car)
          </h3>
          <div className="pd-dim-grid">
            <DimensionStat
              label="Car dimensions (W×D)"
              value={
                recommendedDimensions.carWidth && recommendedDimensions.carDepth
                  ? `${recommendedDimensions.carWidth} × ${recommendedDimensions.carDepth} mm`
                  : "—"
              }
            />
            <DimensionStat
              label="Car area"
              value={
                recommendedDimensions.carArea
                  ? `${formatNumber(recommendedDimensions.carArea, 2)} m²`
                  : "—"
              }
            />
            <DimensionStat
              label="Shaft dimensions (W×D)"
              value={
                recommendedDimensions.shaftWidth && recommendedDimensions.shaftDepth
                  ? `${recommendedDimensions.shaftWidth} × ${recommendedDimensions.shaftDepth} mm`
                  : "—"
              }
            />
            <DimensionStat
              label="Shaft area"
              value={
                recommendedDimensions.shaftArea
                  ? `${formatNumber(recommendedDimensions.shaftArea, 2)} m²`
                  : "—"
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}

function buildCurveData(allResults) {
  if (!Array.isArray(allResults)) return [];
  const byCount = new Map();
  allResults.forEach((item) => {
    if (!item) return;
    const wait = item?.performance?.avg_wait_time_s;
    if (!Number.isFinite(wait)) return;
    const current = byCount.get(item.num_lifts);
    if (!current || wait < current.wait) {
      byCount.set(item.num_lifts, {
        lifts: item.num_lifts,
        wait,
        compliant: Boolean(item.bco_compliant && item.cibse_compliant),
      });
    }
  });
  return Array.from(byCount.values())
    .sort((a, b) => a.lifts - b.lifts)
    .map((point) => ({
      ...point,
      wait: Number(point.wait.toFixed(1)),
    }));
}

function buildHeatmapData(allResults) {
  if (!Array.isArray(allResults) || allResults.length === 0) {
    return { counts: [], rows: [] };
  }

  const counts = new Set();
  const bySize = new Map();

  allResults.forEach((item) => {
    if (!item) return;
    const wait = item?.performance?.avg_wait_time_s;
    if (!Number.isFinite(wait)) return;
    counts.add(item.num_lifts);
    const key = item.lift_size_key || "Option";
    if (!bySize.has(key)) bySize.set(key, new Map());
    const row = bySize.get(key);
    const existing = row.get(item.num_lifts);
    if (!existing || wait < existing.wait) {
      row.set(item.num_lifts, {
        wait,
        compliant: Boolean(item.bco_compliant && item.cibse_compliant),
      });
    }
  });

  const sortedCounts = Array.from(counts).sort((a, b) => a - b);

  const preferredSizeOrder = [
    "8 Persons / 630 kg",
    "10 Persons / 800 kg",
    "13 Persons / 1000 kg",
    "17 Persons / 1275 kg",
    "21 Persons / 1600 kg",
    "26 Persons / 2000 kg",
  ];

  const rows = Array.from(bySize.entries())
    .sort((a, b) => {
      const indexA = preferredSizeOrder.indexOf(a[0]);
      const indexB = preferredSizeOrder.indexOf(b[0]);
      if (indexA === -1 && indexB === -1) {
        return a[0].localeCompare(b[0]);
      }
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    })
    .map(([size, values]) => ({
      size,
      cells: sortedCounts.map((count) => {
        const cell = values.get(count);
        if (!cell) return null;
        return {
          wait: Number(cell.wait.toFixed(1)),
          compliant: cell.compliant,
        };
      }),
    }));

  return { counts: sortedCounts, rows };
}

function deriveLiftDimensions(sizeKey, speed) {
  if (!sizeKey) return null;
  const option = findKoneLiftOption(sizeKey, speed);
  if (!option) return null;
  const carArea = toSquareMetres(option?.car?.width_mm, option?.car?.depth_mm);
  const shaftArea = toSquareMetres(option?.shaft?.width_mm, option?.shaft?.depth_mm);
  return {
    carWidth: option?.car?.width_mm || null,
    carDepth: option?.car?.depth_mm || null,
    carArea: Number.isFinite(carArea) ? carArea : null,
    shaftWidth: option?.shaft?.width_mm || null,
    shaftDepth: option?.shaft?.depth_mm || null,
    shaftArea: Number.isFinite(shaftArea) ? shaftArea : null,
  };
}

/* ---------------- Stats + subcomponents ---------------- */

function StatBox({ icon, label, value, sub, tone }) {
  return (
    <div className="pd-statbox">
      <div className="pd-statbox__label-row">
        {icon}
        <span>{label}</span>
      </div>
      <div
        className={`pd-statbox__value ${
          tone === "good"
            ? "is-good"
            : tone === "warn"
            ? "is-warn"
            : tone === "bad"
            ? "is-bad"
            : ""
        }`}
      >
        {value}
      </div>
      <div className="pd-statbox__sub">{sub}</div>
    </div>
  );
}

function DimensionStat({ label, value }) {
  return (
    <div className="pd-dim-stat">
      <div className="pd-label-small">{label}</div>
      <div className="pd-dim-stat__value">{value}</div>
    </div>
  );
}

function SolutionHeatmap({ data }) {
  const counts = data?.counts || [];
  const rows = data?.rows || [];

  const classify = (wait) => {
    if (!Number.isFinite(wait)) return "";
    if (wait < BCO_WAIT_TARGET_SECONDS) return "is-good";
    if (wait < CIBSE_WAIT_TARGET_SECONDS) return "is-warn";
    return "is-bad";
  };

  return (
    <div className="pd-heatmap">
      <h3 className="pd-panel-subtitle">
        Compliance matrix (size × number of lifts)
      </h3>

      {counts.length === 0 || rows.length === 0 ? (
        <p className="pd-muted">Run the simulation to populate the matrix.</p>
      ) : (
        <div className="pd-heatmap__table-wrap">
          <table className="pd-heatmap-table">
            <thead>
              <tr>
                <th>Lift size / lifts</th>
                {counts.map((n) => (
                  <th key={n}>{n} lifts</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.size}>
                  <td className="pd-heatmap__size">{row.size}</td>
                  {row.cells.map((cell, idx) => (
                    <td key={`${row.size}-${counts[idx] ?? idx}`}>
                      {cell ? (
                        <div className={`pd-heatmap-cell ${classify(cell.wait)}`}>
                          {cell.wait}s
                        </div>
                      ) : (
                        <div className="pd-heatmap-cell">—</div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pd-heatmap-legend">
        <div>
          <span className="pd-legend-swatch is-good" />
          Target (&lt;25s)
        </div>
        <div>
          <span className="pd-legend-swatch is-warn" />
          Tolerable (25–&lt;30s)
        </div>
        <div>
          <span className="pd-legend-swatch is-bad" />
          Non-compliant (≥30s)
        </div>
      </div>
    </div>
  );
}

function ZoningStrategy({
  numFloors,
  entranceFloor,
  numLifts,
  highRiseStart,
  dropOff,
}) {
  const totalFloors = Number.isFinite(numFloors) && numFloors > 0 ? numFloors : 0;
  const entryFloor = Number.isFinite(entranceFloor) ? entranceFloor : 0;
  const totalLifts = Number.isFinite(numLifts) && numLifts > 0 ? numLifts : 0;

  if (totalFloors === 0 || totalLifts === 0) {
    return (
      <div className="pd-zoning">
        <h3 className="pd-panel-subtitle">Lift zoning diagram</h3>
        <p className="pd-muted">Add floor and lift data to view zoning suggestions.</p>
      </div>
    );
  }

  const floors = Array.from({ length: totalFloors }, (_, i) => totalFloors - 1 - i);

  let strategyType = "general";
  let lowRiseTop = 0;
  const liftColumns = [];

  const pushColumn = (variant, servesFloor) => {
    liftColumns.push({
      id: `lift-${liftColumns.length + 1}`,
      label: `Lift ${liftColumns.length + 1}`,
      variant,
      servesFloor,
    });
  };

  if (highRiseStart && highRiseStart > 0 && highRiseStart < totalFloors) {
    strategyType = "zoned";
    lowRiseTop = highRiseStart - 1;
    const lowCount = Math.max(1, Math.floor(totalLifts / 2));
    const highCount = Math.max(1, totalLifts - lowCount);
    const servesLow = (f) => f >= entryFloor && f <= lowRiseTop;
    const servesHigh = (f) => f === entryFloor || f >= highRiseStart;
    for (let i = 0; i < lowCount && liftColumns.length < totalLifts; i += 1) {
      pushColumn("low", servesLow);
    }
    for (let i = 0; i < highCount && liftColumns.length < totalLifts; i += 1) {
      pushColumn("high", servesHigh);
    }
  } else if (dropOff && dropOff > 0 && dropOff < totalFloors - 1) {
    strategyType = "dropoff";
    const topCount = Math.max(1, Math.floor(totalLifts / 2));
    const mainCount = Math.max(1, totalLifts - topCount);
    const servesMain = (f) => f <= dropOff;
    const servesTop = (f) => f === entryFloor || f > dropOff;
    for (let i = 0; i < mainCount && liftColumns.length < totalLifts; i += 1) {
      pushColumn("low", servesMain);
    }
    for (let i = 0; i < topCount && liftColumns.length < totalLifts; i += 1) {
      pushColumn("high", servesTop);
    }
  } else {
    const servesAll = () => true;
    for (let i = 0; i < totalLifts; i += 1) {
      pushColumn("general", servesAll);
    }
  }

  if (liftColumns.length < totalLifts) {
    const servesAll = () => true;
    while (liftColumns.length < totalLifts) {
      pushColumn("general", servesAll);
    }
  }

  const getFloorLabel = (floorIndex) => {
    if (floorIndex === entryFloor) return "G";
    if (floorIndex < entryFloor)
      return `B${entryFloor - floorIndex}`;
    return `${floorIndex}`;
  };

  return (
    <div className="pd-zoning">
      <h3 className="pd-panel-subtitle">Lift zoning diagram</h3>

      <div className="pd-zoning__body">
        <div className="pd-zoning__banks">
          {liftColumns.map((column) => (
            <LiftBank
              key={column.id}
              label={column.label}
              floors={floors}
              servesFloor={column.servesFloor}
              variant={column.variant}
            />
          ))}
        </div>

        <div className="pd-zoning__floors">
          {floors.map((f) => (
            <div
              key={f}
              className={`pd-zoning__floor ${
                f === entryFloor ? "is-entrance" : ""
              }`}
            >
              Floor {getFloorLabel(f)}
            </div>
          ))}
        </div>
      </div>

      <div className="pd-zoning-legend">
        {strategyType === "zoned" && (
          <>
            <div>
              <span className="pd-legend-swatch is-low" />
              Low rise bank
            </div>
            <div>
              <span className="pd-legend-swatch is-high" />
              High rise bank
            </div>
          </>
        )}
        {strategyType === "dropoff" && (
          <>
            <div>
              <span className="pd-legend-swatch is-low" />
              Main bank
            </div>
            <div>
              <span className="pd-legend-swatch is-high" />
              Top lifts
            </div>
          </>
        )}
        {strategyType === "general" && (
          <div>
            <span className="pd-legend-swatch is-general" />
            General-purpose bank
          </div>
        )}
      </div>
    </div>
  );
}

function LiftBank({ label, floors, servesFloor, variant }) {
  return (
    <div className="pd-liftbank">
      <div className="pd-liftbank__shaft">
        {floors.map((f) => (
          <div
            key={f}
            className={`pd-liftbank__cell ${
              servesFloor(f) ? `is-${variant}` : ""
            }`}
          />
        ))}
      </div>
      <div className="pd-liftbank__label">{label}</div>
    </div>
  );
}
